import { Nullable } from 'frontend/types';

const NOOP = () => undefined;

interface Waiter {
  resolve: () => void;
  reject: () => void;
}

export const REPLACE_ERROR = new Error('replace with a newer call');

export class AsyncThrottle {
  //
  // Continuously process the latest event in a series.
  //
  // Used when only one event can be processed at a time, and processing is slow relative to the frequency of events.
  //
  // This diagram show an incoming stream of events (lowercase) and the events being processed (uppercase)
  // (dots represent processing time)
  //
  //   Event stream: abc defg h  i  jkl  mn op
  //   Processing:   A...D...G..H...J..L...N...P...
  //
  callInProgress = false;
  waiter: Nullable<Waiter> = null;

  async throttle(func: () => Promise<void>): Promise<void> {
    if (this.callInProgress) {
      //
      // Wait for the in-progress call to complete.  If there's already a waiter,
      // cancel it and replace it with a new one.
      //
      if (this.waiter) {
        this.waiter.reject();
        this.waiter = null;
      }
      await new Promise<void>((resolve, reject) => {
        this.waiter = {
          resolve,
          reject: () => reject(REPLACE_ERROR),
        };
      });
    }

    this.callInProgress = true;

    await func().finally(() => {
      this.callInProgress = false;
      if (this.waiter) {
        this.waiter.resolve();
        this.waiter = null;
      }
    });
  }
}
